import React, { useState } from 'react'
import { 
  FiX, 
  FiUser, 
  FiMail, 
  FiKey, 
  FiShield, 
  FiCheck,
  FiSave,
  FiUserPlus,
  FiLock,
  FiAward,
  FiSettings,
  FiEdit2,
  FiRefreshCw,
  FiEye,
  FiEyeOff
} from "react-icons/fi";
import { MdOutlineAdminPanelSettings, MdOutlineClose, MdOutlineEdit } from "react-icons/md";
import InputField from '../../../components/ui/Input'
import Select from 'react-select';

const EditModal = ({
    onCancel,
    onSubmit,
    inputData,
    multiSelectOptions,
    roleType
}) => {

    const [editedData, setEditedData] = useState({
        name: inputData?.userName || '',
        roleType: inputData?.role || '',
        email: inputData?.email || '',
        password: '',
        permissions: inputData?.access ?
            multiSelectOptions.filter(option =>
                inputData.access.includes(option.value)
            ) :
            []
    });

    const [error, setError] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    // Custom styles for React Select
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#715EFE' : '#D1D5DB',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(113, 94, 254, 0.2)' : 'none',
            borderRadius: '0.5rem',
            padding: '0.125rem 0',
            minHeight: '42px',
            '&:hover': {
                borderColor: '#715EFE',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#715EFE' : state.isFocused ? '#F3F0FF' : 'white',
            color: state.isSelected ? 'white' : '#1F2937',
            '&:hover': {
                backgroundColor: state.isSelected ? '#715EFE' : '#F3F0FF',
            },
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#F3F0FF',
            borderRadius: '0.375rem',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#715EFE',
            fontWeight: 500,
            fontSize: '0.813rem',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#715EFE',
            '&:hover': {
                backgroundColor: '#715EFE',
                color: 'white',
                borderRadius: '0 0.375rem 0.375rem 0',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#9CA3AF',
            fontSize: '0.875rem',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 50,
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1F2937',
            fontSize: '0.875rem',
        }),
        input: (provided) => ({
            ...provided,
            padding: '0',
            margin: '0',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0.125rem 0.75rem',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            padding: '0 0.75rem',
        }),
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedData(prev => ({
            ...prev,
            [name]: value
        }));

        if (error[name]) {
            setError(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleMultiSelectChange = (selectedOptions) => {
        setEditedData(prev => ({
            ...prev,
            permissions: selectedOptions || []
        }));

        if (error.permissions) {
            setError(prev => ({
                ...prev,
                permissions: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!editedData.name?.trim()) newErrors.name = 'Name is required';
        if (!editedData.roleType?.trim()) newErrors.roleType = 'Role type is required';
        if (!editedData.email?.trim()) newErrors.email = 'Email is required';
        if (editedData.permissions.length === 0) newErrors.permissions = 'At least one permission is required';

        setError(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const payload = {
            _id: inputData._id,
            userName: editedData.name,
            role: editedData.roleType,
            email: editedData.email,
            password: editedData.password || inputData.password,
            access: editedData.permissions.map(permission => permission.value)
        };

        onSubmit(payload);
    };

    // Get role icon
    const getRoleIcon = (role) => {
        if (role?.value === 'super_admin' || role === 'super_admin') return <FiAward className="w-4 h-4" />;
        if (role?.value === 'sub_admin' || role === 'sub_admin') return <FiShield className="w-4 h-4" />;
        return <FiUser className="w-4 h-4" />;
    };

    // Get role badge color
    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'super_admin':
                return 'bg-purple-100 text-purple-700 border-purple-200';
            case 'sub_admin':
                return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'admin':
                return 'bg-emerald-100 text-emerald-700 border-emerald-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    // Custom option renderer
    const formatOptionLabel = (option) => (
        <div className="flex items-center gap-2">
            {getRoleIcon(option)}
            <span>{option.label}</span>
        </div>
    );

    const isFormValid = editedData.name && editedData.email && editedData.roleType && editedData.permissions?.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
                            <MdOutlineEdit className="w-5 h-5 text-[#715EFE]" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Edit Admin</h3>
                            <p className="text-xs text-gray-400 hidden sm:block">Update admin details and permissions</p>
                        </div>
                    </div>
                    <button
                        className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                        onClick={onCancel}
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-4">
                        {/* Name & Email Grid */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                                    <FiUser className="w-4 h-4 text-gray-400" />
                                    <span>Full Name</span>
                                    <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="name"
                                        value={editedData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                                    />
                                    <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                </div>
                                {error.name && <p className="mt-1 text-sm text-red-500">{error.name}</p>}
                            </div>
                            <div>
                                <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                                    <FiMail className="w-4 h-4 text-gray-400" />
                                    <span>Email Address</span>
                                    <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="email"
                                        name="email"
                                        value={editedData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                                    />
                                    <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                </div>
                                {error.email && <p className="mt-1 text-sm text-red-500">{error.email}</p>}
                            </div>
                        </div>

                        {/* Role Selection */}
                        <div>
                            <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                                <FiShield className="w-4 h-4 text-gray-400" />
                                <span>Role</span>
                                <span className="text-red-500">*</span>
                            </label>
                            <Select
                                options={roleType}
                                value={roleType.find(r => r.value === editedData.roleType)}
                                onChange={(selectedOption) => setEditedData(prev => ({
                                    ...prev,
                                    roleType: selectedOption ? selectedOption.value : ''
                                }))}
                                className="basic-single"
                                classNamePrefix="select"
                                styles={customSelectStyles}
                                formatOptionLabel={formatOptionLabel}
                                placeholder="Select a role..."
                                isSearchable={false}
                            />
                            {error.roleType && <p className="mt-1 text-sm text-red-500">{error.roleType}</p>}
                        </div>

                        {/* Current Role Badge */}
                        {inputData?.role && (
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <span className="text-xs font-medium text-gray-500">Current Role:</span>
                                <span className={`
                                    inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
                                    ${getRoleBadgeColor(inputData.role)}
                                `}>
                                    {getRoleIcon(inputData.role)}
                                    {inputData.role?.replace(/_/g, ' ') || 'N/A'}
                                </span>
                            </div>
                        )}

                        {/* Permissions Selection */}
                        <div>
                            <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                                <FiSettings className="w-4 h-4 text-gray-400" />
                                <span>Access Permissions</span>
                                <span className="text-red-500">*</span>
                            </label>
                            <Select
                                isMulti
                                options={multiSelectOptions}
                                value={editedData.permissions}
                                onChange={handleMultiSelectChange}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                styles={customSelectStyles}
                                placeholder="Select permissions..."
                            />
                            {error.permissions && <p className="mt-1 text-sm text-red-500">{error.permissions}</p>}
                        </div>

                        {/* Password */}
                        <div>
                            <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                                <FiKey className="w-4 h-4 text-gray-400" />
                                <span>Password</span>
                                <span className="text-xs text-gray-400 font-normal">(Optional - leave blank to keep current)</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={editedData.password}
                                    onChange={handleInputChange}
                                    placeholder="Enter new password (optional)"
                                    className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                                />
                                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                >
                                    {showPassword ? (
                                        <FiEyeOff className="w-4 h-4" />
                                    ) : (
                                        <FiEye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            {error.password && <p className="mt-1 text-sm text-red-500">{error.password}</p>}
                            <p className="mt-1 text-xs text-gray-400">Leave blank to keep the current password</p>
                        </div>

                        {/* Form validity indicator */}
                        {!isFormValid && (
                            <div className="flex items-center gap-2 p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
                                <FiX className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                <span>Please fill in all required fields to update the admin</span>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
                        <button
                            type="button"
                            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
                                isFormValid 
                                    ? 'bg-gradient-to-r from-[#855DF6] to-[#715EFE] hover:shadow-lg hover:shadow-[#715EFE]/25 hover:-translate-y-0.5' 
                                    : 'bg-gray-300 cursor-not-allowed'
                            }`}
                            disabled={!isFormValid}
                        >
                            <FiEdit2 className="w-4 h-4" />
                            <span>Update Admin</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default EditModal